import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';

import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import {
  catchError,
  combineLatest,
  EMPTY,
  filter,
  finalize,
  forkJoin,
  merge,
  mergeMap,
  Observable,
  of,
  Subscription,
  switchMap,
  take,
  tap,
} from 'rxjs';
import cloneDeep from 'lodash/cloneDeep';
import moment from 'moment';
import { Moment } from 'moment-timezone/moment-timezone';

import { ICapacity, Resource, ResourceAssignedToFloorplan } from '@shared/resource/models';
import { Schedule } from '@shared/schedule/models';
import { Party } from '@shared/party/models';
import { BlockedTimeResult } from '@shared/blocked-time/models';
import { ServiceWithAvailability } from '@shared/service/models';
import { PartySource, PartyTypes } from '@shared/party/enums';
import { Restaurant } from '@shared/restaurant/models';
import { isUsOrCaPhoneNumber, useWhatsApp } from '@shared/sms/functions';
import { mergeCustomFields, canBypassNewPartyValidation, partyCriticalFieldsUnchanged, PartyCriticalFields, partyIdsEqual, isPartySeated } from '@shared/party';
import { describeOutOfBusinessHours } from '@shared/schedule/functions';
import { MS_PER_MIN } from '@shared/core/constants';
import { ResourceSuggestionOption } from '@shared/resource/enums';
import { AppIcon } from '@tablesready/frontend-icons';
import { AlertsService, IAlertTypes } from '@tablesready/frontend-alerts-bar';
import {
  ConfigurePartyService,
  CustomDateParserFormatterProvider,
  CustomTimepickerModelAdapterProvider,
  DateTimeService,
  IParty,
  noWhitespaceValidator,
  ProviderScheduleService,
  TelService,
  ValidationService,
  calculateShortenedDurationState,
  getServiceOptionNameWithWarning,
  ShortenedDurationState,
} from '@tablesready/common-ui';
import { CustomerInfo } from '@tablesready/frontend-party-customer-info';
import { FrontendModalService } from '@tablesready/frontend-modal';
import {
  ActivePartiesService,
  DateService,
  MarkPartyService,
  ResourcesService,
  ServicesService,
  AutoDurationRecalculator
} from '../../services';
import { CustomersService } from '../../customers/services';
import { AddEditPartyService } from '../../services/add-edit-party.service';
import { WaitTimeService } from '@tablesready/frontend-party-observer';
import { PartiesService } from '@tablesready/frontend-api-services';

/**
 * Is used on waitlist pages only
 */
@Component({
  selector: 'tablesready-add-edit-party-reactive',
  templateUrl: './add-edit-party.component.html',
  styleUrls: ['./add-edit-party.component.scss'],
  providers: [
    CustomDateParserFormatterProvider,
    CustomTimepickerModelAdapterProvider,
  ],
  standalone: false,
})
// TODO: disable date and time if selectedTimeMode is true
export class AddEditPartyComponent implements OnInit, OnDestroy {
  @Input() partyToEdit: IParty;
  @Input() prefillFields?: Partial<Party>;
  @Input() userSchedule;
  @Input() user: Restaurant;
  @Input() parties: IParty[];
  @Input() selectedServices;
  @Input() showSelectResourceMessage: boolean;
  @Input() selectedResource: Resource;
  @Input() selectedTime: number;
  @Input() selectedEndTime: number;
  @Input() selectedDate: NgbDateStruct;
  @Input() transparentMode = false;
  @Input() availableOnBusinessPlan = false;

  title = 'Add ';
  angularticsActionType: string;
  selectedTimeMode = false;
  isSubmitting = false;
  isSavingParty = false;
  resourcesInDropdown: ResourceAssignedToFloorplan[] = [];
  servicesInDropdown: ServiceWithAvailability[] = [];
  // Shortened duration tracking
  shortenedDurationState: ShortenedDurationState | null = null;
  shortenedDurationConfirmed = false;

  shortestPhoneDigits = 4;
  originalQuotedTime = 0;
  timezone: string;
  currentTime: Moment;
  isWorkTime = false;
  resourceSizeAlert: string | null = null;
  generalSchedule: Schedule[];
  reservationMinutesPast = 420;
  icon = AppIcon;
  workHours: Array<[number, number]>;
  showOverlappingAlert;
  showResourceAlert = false;
  availablePartyDuration;
  availablePartyDurationInMin;
  removeServices = false;
  removeResources = false;
  overrideNoResources = false;
  overrideIsWorkTime = false;
  overrideBlockedTime = false;
  blockedTime?: BlockedTimeResult;
  // Date
  minDate: NgbDateStruct;
  maxDate: NgbDateStruct;
  noteOptions: string[] | string;
  serviceSettingsLabel: string;
  resourceSettingsLabel: string;
  displayOptionCustomerLabel: string;
  displayOptionsReservationsLabel: string;
  displayOptionsReservationsLabelPlural: string;
  servicesEnabled: boolean;
  form: UntypedFormGroup;
  noAvailableServices = false;
  isAvailableTime: boolean = false;
  isSubmitDisabled = true;
  resourceSuggestionOption = ResourceSuggestionOption;
  isNoServices = false;
  customer: CustomerInfo = null;

  private overlappingParty: Party[] = [];
  private allowMultipleParties: boolean;
  private resourcesEnabled: boolean;
  private onlineReservationEnabled: boolean;
  private isEditMode = false;
  private originalCritical: PartyCriticalFields | null = null;
  private hasDisabledServicesOnInit = false;
  private readonly lockedControlNames: string[] = ['services', 'resources', /*'date', 'time'*/];
  private readonly durationRecalc = new AutoDurationRecalculator({
    getServices: () => this.servicesInDropdown,
    getSelectedServiceIds: () => this.getGroupField('services').value,
    getPartySize: () => this.getGroupField('size').value,
    getDuration: () => this.getGroupField('duration').value,
    applyDuration: (duration) => this.getGroupField('duration').setValue(duration, { emitEvent: false }),
  });

  @ViewChild('confirmModalContent') confirmModalContentRef: ElementRef;
  @ViewChild('phoneNumber') phoneNumberField: ElementRef;

  private readonly subscriptions = new Subscription();

  constructor(
    private readonly alertsService: AlertsService,
    private readonly waitTimeService: WaitTimeService,
    private readonly dateService: DateService,
    private readonly partiesService: PartiesService,
    private readonly configureParty: ConfigurePartyService,
    private readonly modalController: FrontendModalService,
    private readonly telService: TelService,
    private readonly dateTimeService: DateTimeService,
    private readonly providerScheduleService: ProviderScheduleService,
    private readonly markPartyService: MarkPartyService,
    private readonly customerService: CustomersService,
    private readonly formBuilder: FormBuilder,
    private readonly validationService: ValidationService,
    private readonly addEditPartyService: AddEditPartyService,
    private readonly servicesService: ServicesService,
    private readonly resourcesService: ResourcesService,
    private readonly activePartiesService: ActivePartiesService,
  ) { }

  isOutOfBusinessHours(): boolean {
    // Editing an existing party must never re-run new-party business-hours validation —
    // it was already valid when saved; only a brand-new party needs this check.
    if (this.isEditMode && isPartySeated(this.partyToEdit)) {
      return false;
    }

    return !this.form.pristine
      && !this.isWorkTime
      && !this.overrideIsWorkTime;
  }

  getOutOfBusinessHoursMessage(): string {
    return describeOutOfBusinessHours(
      this.currentTime.valueOf(),
      this.getGroupField('duration').value || 0,
      this.workHours || [],
      this.timezone,
    );
  }

  private canBypassWorkTime(): boolean {
    return canBypassNewPartyValidation(
      this.isEditMode, this.partyToEdit, this.originalCritical, () => this.snapshotCritical(),
    );
  }

  private captureOriginalCritical(): void {
    // quotedTime: compare the reactive form's quoted_time control directly. It holds a delta
    // (quoted_time - now) that stays static unless the user edits the time picker, so it is
    // drift-free — unlike currentTime, which is recomputed as now + delta on every time change.
    this.originalCritical = this.snapshotCritical();
  }

  private snapshotCritical(): PartyCriticalFields {
    const rg = this.form.get('requestGroup').value;

    return {
      size: rg.size,
      duration: rg.duration,
      quotedTime: rg.quoted_time,
      services: [...(rg.services ?? [])],
      resources: [...(rg.resources ?? [])],
      seatedTime: this.partyToEdit.seated_time ?? null,
      checkinTime: this.partyToEdit.checkin_time ?? null,
    };
  }

  private criticalFieldsUnchanged(): boolean {
    return partyCriticalFieldsUnchanged(this.originalCritical, () => this.snapshotCritical());
  }

  /**
   * Write a duration the form does not have yet. A party added through a public page arrives
   * without one (the backend only derives a duration when it picks the service itself), and the
   * Duration field is hidden while the value is 0 — so the field would never appear.
   *
   * Filling a blank is not a user edit: the baseline moves with it, so the party still counts as
   * untouched and its availability is not re-validated. Recalculating over a duration the party
   * already has stays off-limits, which is what the untouched check is there for.
   */
  private fillMissingDuration(duration: number): void {
    const wasUnchanged = this.criticalFieldsUnchanged();
    this.getGroupField('duration').setValue(duration, {emitEvent: false});

    if (wasUnchanged && this.originalCritical) {
      this.originalCritical = { ...this.originalCritical, duration };
    }
  }

  get requestGroup(): AbstractControl {
    return this.form.get('requestGroup');
  }

  get isReservationForm(): boolean {
    return this.requestGroup.get('type').value === PartyTypes.reservation;
  }

  getNoAvailableServices(): boolean {
    // A seated party is never re-validated against current availability (it was necessarily
    // seated in the past), so an empty dropdown says nothing about it — and this warning offers
    // no override, so leaving it on would block the edit outright.
    return this.servicesEnabled &&
      this.servicesInDropdown.length === 0 &&
      !isPartySeated(this.partyToEdit);
  }

  /**
   * Whether the schedule warning with its "Override?" checkbox is on screen. Bound by the template
   * so the warning and the Confirm button can never disagree: Confirm is disabled while this shows
   * (tick Override first) and — just as important — is not blocked by business hours when it
   * doesn't, so an existing party stays editable on a closed day.
   */
  showBusinessHoursWarning(): boolean {
    return this.checkIfOutOfBusinessHours() && this.isNoServices;
  }

  /** Same contract for the "no services available" warning, which offers no override at all. */
  showNoServicesWarning(): boolean {
    return !this.checkIfOutOfBusinessHours() && this.noAvailableServices && !this.isSubmitting;
  }

  getIsAvailableTime() {
    return this.isWorkTime &&
      this.showResourceAlert &&
      !this.resourcesInDropdown.length &&
      this.resourcesEnabled;
  }

  ngOnInit(): void {
    this.initModalProp();
    this.initForm();
    this.minDate = this.dateTimeService.momentToNgbDate(this.currentTime);
    this.maxDate = {
      ...this.minDate,
      year: this.minDate.year + 1,
    };
    if (this.partyToEdit) {
      this.initEditMode();
    } else if (this.selectedTime && this.selectedResource) {
      this.initAddModeWithSelectedResourceAndTime(this.selectedTime);
    } else {
      this.initAddMode();
    }

    this.title += this.user.displayOptions?.labels?.customer || 'Customer';
  }

  initModalProp() {
    this.noteOptions = this.user?.noteOptions || [];
    this.serviceSettingsLabel = this.user?.serviceSettings?.label || 'service';
    this.resourceSettingsLabel = this.user.resourceSettings?.label || 'resource';
    this.displayOptionCustomerLabel = this.user.displayOptions?.labels?.customer || 'customer';
    this.displayOptionsReservationsLabel = this.user.displayOptions?.labels?.reservation || 'reservation';
    this.displayOptionsReservationsLabelPlural = this.user.displayOptions?.labels?.reservation_plural || 'reservations';
    this.allowMultipleParties = this.user?.bookingOptions?.allowMultipleParties;
    this.onlineReservationEnabled = this.user?.bookingOptions?.onlineReservationsEnabled;
    this.timezone = this.user.timezone;
    this.generalSchedule = (this.userSchedule || []).filter(schedule => schedule.default);
    const subscription = combineLatest([
      this.resourcesService.isEnabled(),
      this.servicesService.isEnabled()
    ])
      .pipe(tap(([resourcesEnabled, servicesEnabled]) => {
        this.resourcesEnabled = resourcesEnabled;
        this.servicesEnabled = servicesEnabled;
      }))
      .subscribe();
    this.subscriptions.add(subscription);

    try {
      this.generalSchedule[0].hours;
    } catch (e) {
      this.alertsService.add({
        time: Date.now(),
        type: IAlertTypes.danger,
        heading: 'Please fill in work schedule in Settings -> Schedules',
        body: '',
      });
    }

    this.currentTime = moment.tz(this.timezone);
    this.showSelectResourceMessage = this.showSelectResourceMessage || false;
    this.overrideNoResources = !(this.user.serviceSettings?.enabled || this.user.bookingOptions?.onlineReservationsEnabled);
  }

  initEditMode() {
    this.title = 'Edit ';
    this.angularticsActionType = 'edited reservation';
    this.selectedTimeMode = false;
    this.isEditMode = true;
    // const quotedTime = moment.tz(this.partyToEdit.quoted_time, this.timezone);
    // Only enabled services/resources are shown as selected and offered in the dropdown — a
    // disabled assignment is never user-visible or user-selectable. The true original ids
    // (this.partyToEdit.services/.resources) remain available for the autoselect guard below and
    // for saveParty(), which restores them on save if the user never touched these fields, so a
    // disabled assignment is preserved rather than silently dropped by an unrelated edit.
    const enabledServices = this.servicesService.getEnabledByIds(this.partyToEdit.services);
    const hasDisabledServices = enabledServices.length !== (this.partyToEdit.services || []).length;
    this.hasDisabledServicesOnInit = hasDisabledServices;
    const services = enabledServices.map(service => service._id);
    const resources = this.resourcesService.getEnabledByIds(this.partyToEdit.resources).map(resource => resource._id);
    const duration = hasDisabledServices
      ? this.dateService.checkServicesDuration(enabledServices, services, this.partyToEdit.size)
      : undefined;
    this.durationRecalc.setBaseline(
      this.dateService.checkServicesDuration(enabledServices, services, this.partyToEdit.size),
      services,
    );
    const data = {
      ...this.partyToEdit,
      phone: this.telService.format(this.partyToEdit.phone, this.user.country, 'custom'),
      services,
      resources,
      ...(duration !== undefined && { duration }),
      requestGroup: {
        ...this.partyToEdit,
        services,
        resources,
        ...(duration !== undefined && { duration }),
        // date: this.dateTimeService.momentToNgbDate(quotedTime),
        // time: quotedTime.format('h:mm A'),
        quoted_time: /*this.pageType === PageType.waitlist ? */Math.round((this.partyToEdit.quoted_time - Date.now()))/* : 0*/,
        overrideNoResources: this.overrideNoResources, // TODO: consider removing component property
        overrideIsWorkTime: this.overrideIsWorkTime, // TODO: consider removing component property
      },
    };

    this.originalQuotedTime = this.partyToEdit.quoted_time;
    this.currentTime = moment(this.partyToEdit.quoted_time).tz(this.timezone);
    this.isWorkTime = true;
    this.form.patchValue(data, {emitEvent: false});
    this.captureOriginalCritical();
    if (!this.partyToEdit.phone || isUsOrCaPhoneNumber(this.partyToEdit.phone) || this.partyToEdit?.source === PartySource.api) {
      this.form.get('messagingProvider').disable({onlySelf: true, emitEvent: false});
    } else {
      this.form.get('messagingProvider').enable({onlySelf: true, emitEvent: false});
    }
    this.subscriptions.add(
      this.checkWorkTime(data.requestGroup)
        .pipe(
          // Reset isSubmitting BEFORE applyAvailabilityResult's patchValue call: that patchValue
          // synchronously re-triggers requestGroupSource's own filter(() => !this.isSubmitting) guard
          // for the next autoselect pass (e.g. service -> resource); resetting it after would drop
          // that re-entrant pass instead of letting it through.
          tap(() => this.isSubmitting = false),
          mergeMap(data => this.applyAvailabilityResult(data)),
          tap(() => this.setFlags()),
        )
        .subscribe()
    );
  }

  initAddModeWithSelectedResourceAndTime(time: number): void {
    this.angularticsActionType = 'added reservation';
    this.selectedTimeMode = true;
    const selectedTime = this.dateTimeService.roundingMinutes(Number(time), 15, this.timezone);
    const party = {
      quoted_time: 0,
      type: PartyTypes.reservation,
      // date: this.selectedDate,
      // time: selectedTime.format('h:mm A'),
      resources: [this.selectedResource._id],
      services: [],
      customFields: {},
      size: Math.max(this.selectedResource.capacity.min, Math.min(this.user.default_size, this.selectedResource.capacity.max)),
    };
    this.currentTime = selectedTime;
    this.form.patchValue(party);
  }

  initAddMode() {
    this.angularticsActionType = 'added reservation';
    this.selectedTimeMode = false;
    let data = {
      customFields: {},
      requestGroup: {
        id: '',
        type: /*this.pageType === PageType.reservation ? PartyTypes.reservation :*/ PartyTypes.walkIn,
        // date: this.selectedDate,
        // date: this.selectedDate || this.dateTimeService.momentToNgbDate(this.currentTime),
        // time: this.pageType === PageType.reservation ? undefined : this.currentTime.format('h:mm A'),
        size: this.user.default_size,
        services: [],
        resources: [],
        duration: 0,
        overrideNoResources: this.overrideNoResources, // TODO: consider removing component property
        overrideIsWorkTime: this.overrideIsWorkTime, // TODO: consider removing component property
        quoted_time: /*this.pageType === PageType.reservation ? 0 : */this.waitTimeService.getTime() * MS_PER_MIN,
      },
    };
    this.currentTime.add(this.waitTimeService.getTime(), 'minutes');
    this.form.patchValue(data);

    setTimeout(() => this.phoneNumberField.nativeElement.focus());
  }

  /**
   * Return result:
   * Record<string, any> - form data has been changed, so we should set and process it.
   * null - nothing to process, we can apply after processing actions like handling alerts.
   * undefined as a return result means that we should stop call processing without any actions.
   *
   * @param values
   * @return Observable<null | Record<string, any> | undefined>
   */
  checkWorkTime(values: Record<string, unknown> = {}): Observable<null | Record<string, any>> {
    if (!this.servicesEnabled && !this.onlineReservationEnabled && this.resourcesEnabled) {
      this.isWorkTime = true;

      return this.getAvailableResourcesServices(values);
    }

    // TODO: this part is for future moving some logic into service
    // if (this.pageType === PageType.reservation && !values.time) {
    //   // this.resourceOverride.emit(values);
    //
    //   return of(undefined);
    // }

    const { hours, exceptions } = this.generalSchedule[0];
    this.workHours = this.providerScheduleService.getWorkingHours(hours, exceptions, this.currentTime.valueOf(), this.timezone);

    let duration = this.getGroupField('duration').value;
    if (duration <= 0) {
      duration = this.dateService.checkServicesDuration(this.servicesInDropdown, values.services as string[], this.getGroupField('size').value);
      this.fillMissingDuration(duration);
    }
    values.duration = duration;

    this.isWorkTime = this.providerScheduleService.comparisonTime(this.currentTime.valueOf(), this.workHours, values.duration as number);

    if (this.servicesEnabled || this.resourcesEnabled) {
      return this.getAvailableResourcesServices(values);
    }

    return of(undefined);
  }

  /**
   * Record<string, any> - form data has been changed, so we should set and process it.
   * null - nothing to process, we can apply after processing actions like handling alerts.
   * undefined as a return result means that we should stop call processing without any actions.
   *
   * @param party
   */
  getAvailableResourcesServices(party): Observable<null | Record<string, any> | undefined> {
    const date = this.currentTime.valueOf();
    const { services = [], resources = [], size, duration, type } = party;
    if (!date && !services && !resources) {
      return of(undefined);
    }

    const { hours, exceptions } = this.generalSchedule[0];
    const hoursWithDate = this.providerScheduleService.getWorkingHours(
      hours,
      exceptions,
      date,
      this.timezone,
    );

    this.setEnabledState(false);

    return this.partiesService.findResourcesServicesForNewParty(
      String(date), // TODO: backend accepts string
      type,
      services,
      resources,
      size,
      duration,
      party._id,
      this.overrideNoResources,
      hoursWithDate,
    )
    .pipe(
      take(1),
      switchMap((data) => {
        this.setEnabledState(true);
        this.showOverlappingAlert = false;
        this.overlappingParty = data?.overlappingParty || [];
        this.blockedTime = data?.blockedTime;
        this.servicesInDropdown = data.availableServices || [];

        const dataToUpdate: { services?: string[]; resources?: string[] } = {};
        if (data?.availableResources) {
          this.resourcesInDropdown = this.addEditPartyService.placeResourcesFitBySizeFirst<ResourceAssignedToFloorplan>(data.availableResources, size);
          // If party.resource is already chosen and does not belong to the resources list (e.g. it
          // was disabled), clear it — but only when something critical actually changed. An edit
          // that never touches services/resources/size/duration/time must leave the existing
          // (possibly since-disabled) assignment alone.
          if (this.resourcesInDropdown.length && resources.length && !this.canBypassWorkTime()) {
            const resourceIds: string[] = resources.filter(resourceId => this.resourcesInDropdown.some(resource => resource._id === resourceId));
            if (resourceIds.sort().join() !== resources.sort().join()) {
              dataToUpdate.resources = resources.filter(resourceId => this.resourcesInDropdown.some(resource => resource._id === resourceId));
            }
          }

          if (this.resourcesEnabled) {
            this.showResourceAlert = true;
          }
        }

        if (this.overlappingParty.length && !(this.allowMultipleParties || this.overrideNoResources)) {
          this.resourcesInDropdown = [];
        }

        // Protect an existing party's already-assigned service/resource from being silently swapped
        // or filled in by this autoselect. These check the party's true original assignment
        // (this.partyToEdit), not the enabled-only `services`/`resources` shown in the dropdown —
        // a disabled assignment is hidden from the picker, but it still counts as "already assigned"
        // for this guard, and only counts as absent when the party truly never had one.
        const blocksServiceAutoselect = this.canBypassWorkTime() && (this.partyToEdit?.services?.length ?? 0) > 0;
        const blocksResourceAutoselect = this.canBypassWorkTime() && (this.partyToEdit?.resources?.length ?? 0) > 0;
        if (this.servicesInDropdown?.length === 1 && !this.removeServices) {
          if (!blocksServiceAutoselect && (!services.length || this.servicesInDropdown[0]._id !== services[0])) {
            // Autoselect service
            const serviceIdToSelect = this.servicesInDropdown[0]._id;
            dataToUpdate.services = [serviceIdToSelect];
          } else if (services.length === 1 && !resources.length && !blocksResourceAutoselect) {
            // Autoselect resource
            this.resourcesInDropdown = this.resourcesInDropdown.filter(resource => resource.services.includes(services[0]));
            if (
              this.user.resourceSettings?.enabled &&
              this.user.resourceSettings?.resourceSuggestion === this.resourceSuggestionOption.auto &&
              this.resourcesInDropdown.length > 0 &&
              !this.removeResources &&
              !this.selectedResource
            ) {
              let resourceIdToSelect: string;

              for (const availableResource of data?.availableResources) {
                if (
                  availableResource.services.includes(services[0]) &&
                  size >= availableResource.capacity.min &&
                  size <= availableResource.capacity.max
                ) {
                  // We don't want to pre-select a resource that doesn't fit party size
                  // And we've already filtered resources by min capacity
                  resourceIdToSelect = availableResource._id;

                  break;
                }
              }
              if (resourceIdToSelect !== undefined) {
                dataToUpdate.resources = [resourceIdToSelect];
              }
            }
          }
        }

        return of(Object.keys(dataToUpdate).length ? dataToUpdate : null);
      }),
      catchError((error) => {
        this.setEnabledState(true);
        if (error.status === 401) {
          this.cancel();
        } else {
          this.alertsService.add({
            time: Date.now(),
            type: IAlertTypes.danger,
            heading: 'Error:',
            body: `resources and services not found: ${error}`,
          });
        }

        // Never pass the error on as form data: an HttpErrorResponse has its own `type`
        // property, so patching it into the form wipes the party type.
        return of(undefined);
      }),
    );
  }

  selectServices(services: string[] = undefined) {
    const {requestGroup: {services: prevServices}} = this.form.value;
    this.removeServices = prevServices.length && services?.length === 0;
    if (this.removeServices || prevServices.sort().toString() !== services.sort().toString()) {
      this.getGroupField('duration').setValue(0, {emitEvent: false});
    }
    // if (this.removeServices) {
    //   this.showResourceAlert = false;
    // }
  }

  selectResources(resources?: string[]): void {
    const {requestGroup: {resources: prevResources}} = this.form.value;
    this.removeResources = prevResources.length > 0 && resources?.length === 0;
  }

  convertQuotedTime(party) {
    const resTime = moment.tz(this.timezone);

    delete party.date;
    delete party.time;
    delete party.className;
    delete party.assignedResources;

    if (party.type === PartyTypes.reservation && this.currentTime.format('x') < resTime.add(-this.reservationMinutesPast, 'minutes').format('x')) {
      party.quoted_time = Number(this.currentTime.format('x'));
      this.isWorkTime = false;

      return;
    }

    const updateWaitTime = !party._id && party.type === PartyTypes.walkIn; // Update wait time if new party
    party.quoted_time = party.quoted_time || 0;
    if (updateWaitTime && !(
      this.originalQuotedTime > 0 &&
      party.quoted_time > this.originalQuotedTime - MS_PER_MIN &&
      party.quoted_time < this.originalQuotedTime + MS_PER_MIN
    )) {
      this.waitTimeService.setTime(Math.round((party.quoted_time - Date.now()) / MS_PER_MIN));
    }
    party.quoted_time = Number(this.currentTime.format('x'));
  }

  showSizeAlert(resourcesCapacity: ICapacity): void {
    this.resourceSizeAlert = this.resourcesInDropdown?.length
      ? this.configureParty.buildResourceSizeAlert({
          actualSize: this.getGroupField('size').value,
          capacity: resourcesCapacity,
          customerLabel: this.displayOptionCustomerLabel,
          resourceLabel: this.resourceSettingsLabel,
          allowMultipleParties: this.allowMultipleParties,
          partySizeForTimeSlots: this.user?.bookingOptions?.partySizeForTimeSlots,
          overlappingParties: this.overlappingParty,
        })
      : null;
  }

  getItemOptionName = (item: ResourceAssignedToFloorplan): string => {
    return this.getItemName(item, true);
  };

  getItemName(item: ResourceAssignedToFloorplan, addCapacity = false): string {
    let result = item?.name || '';

    if (addCapacity && item?.capacity) {
      result += ` (${item.capacity.max}) `;
    }

    if (item?.onFloorplan) {
      result += ` [${item.onFloorplan}]`;
    }

    return result;
  }

  isConfirmPartyButtonDisabled() {
    return (this.showOverlappingAlert || !!this.resourceSizeAlert);
  }

  saveParty(): void {
    const {requestGroup, ...rest} = this.form.value;
    const party = this.partiesService.clean({
      ...rest,
      ...requestGroup
    });
    if (party.phone) {
      party.phone = this.telService.format(party.phone, this.user.country, 'intl');
    }

    // The services/resources fields only ever show/offer enabled ids, so a disabled assignment is
    // never visible to touch. If the user left a field exactly as it was when the form opened,
    // restore the party's true original ids (which may include a since-disabled one) rather than
    // saving the enabled-only value the picker displayed — an edit to an unrelated field (e.g.
    // Notes) must not silently drop a disabled service/resource assignment.
    if (this.isEditMode) {
      if (partyIdsEqual(party.services, this.originalCritical?.services)) {
        party.services = this.partyToEdit.services || [];
      }
      if (partyIdsEqual(party.resources, this.originalCritical?.resources)) {
        party.resources = this.partyToEdit.resources || [];
      }
      // hasDisabledServicesOnInit means initEditMode recalculated duration from only the
      // enabled subset of services (possibly 0), overwriting the party's true duration in the
      // form. If the user never touched the duration field since, save the true original value
      // instead of the recalculated one, or a disabled-then-reenabled service permanently loses
      // its duration.
      if (this.hasDisabledServicesOnInit && party.duration === this.originalCritical?.duration) {
        party.duration = this.partyToEdit.duration;
      }
    }

    this.convertQuotedTime(party);

    party.overrideNoResources = this.overrideNoResources;
    party.overrideBlockedTime = this.overrideBlockedTime;

    if ((!this.showOverlappingAlert && this.isEditMode) || this.canBypassWorkTime()) {
      party.overrideNoResources = true;
    }

    if (party.type !== PartyTypes.reservation && !Boolean(this.partyToEdit)) {
      party.checkin_time = party.checkin_time || Date.now();
    }

    this.savePartyAction(party as IParty);
  }

  cancel(): void {
    void this.modalController.dismiss(null);
  }

  ok() {
    const { phone } = this.form.value;
    if (phone !== '' && this.parties.find(party => party.phone === phone)) {
      this.modalController.create<unknown, string>(this.confirmModalContentRef.nativeElement)
        .pipe(
          take(1),
          tap((res) => {
            if (res === 'Cancel click') {
              return;
            }

            this.actionAfterCloseModal();
          }),
          catchError((error) => {
            this.cancel();

            return of(error);
          }),
        )
        .subscribe();

      return;
    }
    this.actionAfterCloseModal();
  }

  actionAfterCloseModal(): void {
    const { size } = this.form.value;
    if (this.user.waitFields.size && size && typeof size !== 'number') {

      return;
    }

    this.saveParty();
  }

  onShortenedDurationConfirmedChange(value: boolean): void {
    this.shortenedDurationConfirmed = value;
    this.setFlags();
  }

  getIsSubmitDisabled(): boolean {
    // Confirm tracks the warnings the form actually shows — one rule for add and edit alike, so a
    // warning can never sit on screen next to an enabled Confirm (or vice versa):
    //   schedule / no-services      → showBusinessHoursWarning() / showNoServicesWarning()
    //   "no available table"         → isAvailableTime && !overrideNoResources
    //   blocked time                 → blockedTime.blocked && !overrideBlockedTime
    //   overlap + resource-capacity  → isConfirmPartyButtonDisabled()
    //   shortened duration           → requiresConfirmation && !shortenedDurationConfirmed
    // Edit mode differs only in what those predicates report (a seated or non-critical edit is
    // never re-validated), never in which of them are consulted.
    const blockedByWarnings = this.showBusinessHoursWarning() || this.showNoServicesWarning();
    const isAvailableTimeAndNoOverride = Boolean(this.isAvailableTime) && !this.overrideNoResources;
    const isBlockedTimeAndNoOverride =
      this.isReservationForm && Boolean(this.blockedTime?.blocked) && !this.overrideBlockedTime;
    const needsShortenedDurationConfirmation =
      Boolean(this.shortenedDurationState?.requiresConfirmation) && !this.shortenedDurationConfirmed;

    return this.form.invalid ||
      blockedByWarnings ||
      this.isConfirmPartyButtonDisabled() ||
      this.form.pending ||
      this.isSubmitting ||
      isAvailableTimeAndNoOverride ||
      isBlockedTimeAndNoOverride ||
      needsShortenedDurationConfirmation;
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    // this.resourceOverride.complete();
  }

  undoCheckIn(): void {
    const party = cloneDeep(this.partyToEdit);
    party.checkin_time = null;
    party.overrideNoResources = this.overrideNoResources;

    if (this.isEditMode) {
      party.overrideNoResources = true;
    }

    this.savePartyAction(party, 'check-in was canceled');
  }

  undoServed(): void {
    this.isSubmitting = true;
    const options = {
      party_id: this.partyToEdit._id,
      seated_time: null,
      canceled_time: null,
    };

    this.markPartyService.setParty(options)
      .pipe(
        tap(() => {
          this.alertsService.addSuccess(
            `moved to ${this.partyToEdit.type === PartyTypes.togo ? 'To Go' : 'waitlist'}`,
            'Success: ',
          );
          void this.modalController.dismiss();
          this.isSubmitting = false;
        }),
        catchError(err => {
          this.isSubmitting = false;
          this.alertsService.addError('an error occurred during moving party to Waitlist');

          return of(err);
        }),
      ).subscribe();
  }

  // TODO: move logic to from value changes
  checkIfOutOfBusinessHours(): boolean {
    return this.isOutOfBusinessHours() && !this.overrideIsWorkTime;
  }


  isWhatsAppEnabled() {
    return useWhatsApp(this.user);
  }

  getGroupField(name: string) {
    return this.form.get(`requestGroup.${name}`);
  }

  private savePartyAction(party: IParty, successMessage = 'updated successfully'): void {
    if (this.isSavingParty) {
      return;
    }

    this.isSubmitting = true;
    this.isSavingParty = true;
    this.partiesService.saveParty(party)
      .pipe(
        take(1),
        tap((data) => {
          // Eagerly sync store so UI reflects the new/updated party even if
          // the websocket event is lost or delayed. WS handlers dedupe via upsert.
          this.activePartiesService.upsertParty(data);
          void this.modalController.dismiss(data);
          this.alertsService.add({
            time: Date.now(),
            type: IAlertTypes.success,
            heading: 'Success:',
            body: successMessage,
          });
        }),
        catchError((err) => {
          if (err?.reloadData) {
            this.getGroupField('resources').setValue([], {onlySelf: true, emitEvent: false});
          }

          const customer = this.user.displayOptions?.labels?.customer || 'customer';
          this.alertsService.add({
            time: Date.now(),
            type: IAlertTypes.danger,
            heading: 'Error:',
            body: `new ${customer} not added successfully: ${err?.message}`,
          });

          // this.resourceOverride.emit(this.form.value);

          return of(err);
        }),
        finalize(() => {
          this.isSavingParty = false;
          this.isSubmitting = false;
        }),
      )
      .subscribe();
  }


  private initForm() {
    this.form = this.formBuilder.group({
      phone: ['', [this.validationService.phonePatternCountryValidator(this.user.country)]],
      name: ['', [Validators.required, noWhitespaceValidator]],
      note: [''],
      messagingProvider: [null, []],
      customFields: [[]],
      // Changing any of these fields may trigger http request
      requestGroup: this.formBuilder.group({
        _id: [''],
        waitlistId: [''],
        type: [PartyTypes.walkIn],
        size: [this.user.default_size, [Validators.required, Validators.min(1)]],
        services: [[]],
        resources: [[]],
        duration: [0, [Validators.required, Validators.min(0)]],
        overrideNoResources: [false, []],
        overrideIsWorkTime: [false, []],
        overrideBlockedTime: [false, []],
        quoted_time: [0, [Validators.required]],
      }),
    });

    if (this.prefillFields) {
      Object.entries(this.prefillFields).forEach(([key, value]) => {
        const control = this.form.get(key);
        if (control) {
          control.setValue(value);
          control.updateValueAndValidity();
        }
      });
    }

    this.form.get('messagingProvider').disable();

    this.subscriptions.add(this.listenToPhoneChanges().subscribe());

    this.subscriptions.add(
      merge(
        this.getGroupField('size').valueChanges,
        this.getGroupField('services').valueChanges,
      ).subscribe(() => this.durationRecalc.recompute()),
    );

    const requestGroupSource = merge(
      this.listenToServicesChanges(),
      this.listenToResourcesChanges(),
      this.getGroupField('size').valueChanges,
      this.getGroupField('duration').valueChanges,
      this.listenToOverrideNoResources(),
      this.listenToOverrideIsWorkTime(),
      this.listenToOverrideBlockedTime(),
      this.listenToQuotedTimeChanges(),
    ).pipe(
      switchMap(() => this.form.get('requestGroup').valueChanges),
      filter(() => !this.isSubmitting), // TODO: it's temporary solution, timepicker trigger changes twice, so it have to be fixed
      tap(() => this.isSubmitting = true),
      mergeMap(value => this.checkWorkTime(value)),
      // Reset isSubmitting BEFORE applyAvailabilityResult's patchValue call: that patchValue
      // synchronously re-enters this same pipe for the next autoselect pass (e.g. service ->
      // resource), and the filter() above would otherwise drop that pass while isSubmitting is
      // still true from this one.
      tap(() => this.isSubmitting = false),
      mergeMap(data => this.applyAvailabilityResult(data)),
      catchError(error => {
        this.isSubmitting = false;

        return EMPTY;
      }),
    );

    const restColumnsSource = merge(
      this.form.get('phone').valueChanges,
      this.form.get('name').valueChanges,
      this.form.get('note').valueChanges,
      this.form.get('customFields').valueChanges,
    );

    const formSubscription = merge(
      requestGroupSource,
      restColumnsSource,
      this.form.statusChanges,
    )
    .pipe(tap(() => this.setFlags()))
    .subscribe();
    this.subscriptions.add(formSubscription);
  }

  private listenToPhoneChanges(): Observable<unknown> {
    return this.form.get('phone').valueChanges.pipe(
      switchMap(phone => forkJoin([
        of(phone),
        this.customerService.partyPhoneLookup(phone, this.user.country, this.user.partyFields, this.shortestPhoneDigits)
      ])),
      tap(([phone, data]) => {
        let dataToUpdate: Record<string, unknown> = {
          phone: this.telService.format(phone, this.user.country),
        };
        if (isUsOrCaPhoneNumber(phone)) {
          dataToUpdate.messagingProvider = null;
        }
        if (data) {
          dataToUpdate = {
            ...dataToUpdate,
            name: this.form.get('name').value || data.name,
            customFields: mergeCustomFields(this.form.value.customFields, data.customFields),
          };
          this.customer = data.customer;
        } else {
          this.customer = null;
        }
        this.form.patchValue(dataToUpdate, {emitEvent: false});

        if (!phone || isUsOrCaPhoneNumber(phone) || this.partyToEdit?.source === PartySource.api) {
          this.form.get('messagingProvider').disable({onlySelf: true, emitEvent: false});
        } else {
          this.form.get('messagingProvider').enable({onlySelf: true, emitEvent: false});
        }
      })
    )
  }

  private listenToServicesChanges() {
    return this.getGroupField('services').valueChanges
    .pipe(
      switchMap(services => {
        this.selectServices(services);

        return of(services);
      }),
    );
  }

  private listenToResourcesChanges() {
    return this.getGroupField('resources').valueChanges
    .pipe(switchMap(resources => {
      this.selectResources(resources);

      return of(resources);
    }))
  }

  private listenToOverrideNoResources() {
    return this.getGroupField('overrideNoResources').valueChanges.pipe(tap(value => {
      this.overrideNoResources = value;
    }));
  }

  private listenToOverrideIsWorkTime() {
    return this.getGroupField('overrideIsWorkTime').valueChanges.pipe(tap(value => {
      this.overrideIsWorkTime = value;
    }));
  }

  private listenToOverrideBlockedTime() {
    return this.getGroupField('overrideBlockedTime').valueChanges.pipe(tap(value => {
      this.overrideBlockedTime = value;
    }));
  }

  private listenToQuotedTimeChanges() {
    return this.getGroupField('quoted_time').valueChanges.pipe(tap(value => {
      this.currentTime = moment.tz(this.timezone);
      this.currentTime.add(value, 'milliseconds');
    }));
  }

  private setEnabledState(state: boolean) {
    // TODO: enabling/disabling the whole group, now it causes error is size element
    // const control = this.form.get('requestGroup');
    // state ? control?.enable({emitEvent: false}) : control.disable({emitEvent: false});
    for (const controlName of this.lockedControlNames) {
      const control = this.form.get(`requestGroup.${controlName}`);
      state ? control?.enable({emitEvent: false}) : control.disable({emitEvent: false});
    }
  }

  private setFlags() {
    this.isAvailableTime = this.getIsAvailableTime();
    this.noAvailableServices = this.getNoAvailableServices();
    // isNoServices feeds showBusinessHoursWarning(), so it has to be current before
    // getIsSubmitDisabled() reads it.
    this.isNoServices = !this.servicesEnabled || !this.getGroupField('services').value.length;
    this.isSubmitDisabled = this.getIsSubmitDisabled();
  }

  private applyAvailabilityResult(
    data: null | Record<string, any> | undefined,
  ): Observable<null | Record<string, any> | undefined> {
    if (data === undefined) {
      return of(undefined);
    }

    if (data) {
      this.form.get('requestGroup').patchValue(data);

      return of(data);
    }

    const shortenedDurationChanged = this.handleAlerts();
    if (!shortenedDurationChanged) {
      return of(null);
    }

    // The duration just got shortened (see handleAlerts below) — the availability/resources
    // we just fetched were checked against the old, longer duration, so re-check with the
    // corrected one instead of leaving stale results in place.
    return this.checkWorkTime(this.requestGroup.getRawValue())
      .pipe(
        tap((updatedData) => {
          if (updatedData) {
            this.form.get('requestGroup').patchValue(updatedData);
          } else if (updatedData === null) {
            this.handleAlerts();
          }
        }),
      );
  }

  private handleAlerts(): boolean {
    let shortenedDurationChanged = false;
    this.resourceSizeAlert = null;
    const resources = this.getGroupField('resources').value;
    if (this.resourcesEnabled && resources?.length) {
      const resourcesCapacity = this.partiesService.calculateCapacityInSelectedResources(this.resourcesInDropdown, resources);
      this.showSizeAlert(resourcesCapacity);
    }

    const date = this.currentTime.valueOf();
    if (this.overlappingParty.length && !(this.allowMultipleParties || this.overrideNoResources)) {
      this.showOverlappingAlert = true;
      const overlappingParty = this.overlappingParty[0];
      this.availablePartyDuration = overlappingParty.quoted_time - date;
      this.availablePartyDurationInMin = Math.round(this.availablePartyDuration / MS_PER_MIN);
    }

    let duration = this.getGroupField('duration').value;
    if (duration <= 0) {
      const services = this.getGroupField('services').value;
      duration = this.dateService.checkServicesDuration(this.servicesInDropdown, services, this.getGroupField('size').value);
      this.fillMissingDuration(duration);
    }

    // Calculate shortened duration state and apply adjusted duration if needed.
    // In edit mode we never write the adjusted duration back (it would silently change an
    // existing party's duration); the state is still computed for the warning display.
    const selectedServiceIds = this.getGroupField('services')?.value || [];
    const prevRequiresConfirmation = this.shortenedDurationState?.requiresConfirmation ?? false;
    this.shortenedDurationState = calculateShortenedDurationState(this.servicesInDropdown, selectedServiceIds);
    if (!this.canBypassWorkTime() && this.shortenedDurationState.adjustedDurationMs !== null) {
      const currentDuration = this.getGroupField('duration').value;
      if (!currentDuration || currentDuration > this.shortenedDurationState.adjustedDurationMs) {
        this.getGroupField('duration').setValue(this.shortenedDurationState.adjustedDurationMs, { emitEvent: false });
        shortenedDurationChanged = true;
        // Recalculate isWorkTime with the adjusted duration
        this.isWorkTime = this.providerScheduleService.comparisonTime(
          this.currentTime.valueOf(),
          this.workHours,
          this.shortenedDurationState.adjustedDurationMs,
        );
      }
    }

    if (!this.shortenedDurationState.requiresConfirmation) {
      this.shortenedDurationConfirmed = true;
    } else if (!prevRequiresConfirmation) {
      this.shortenedDurationConfirmed = false;
    }

    return shortenedDurationChanged;
  }

  getServiceOptionName = (service: ServiceWithAvailability): string => {
    return getServiceOptionNameWithWarning(service);
  };
}
